import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import userPlacesQueryKeys from "../query-keys/places.query-keys";
import { UpsertPlaceForm } from "../schemas";

export const createPlace = async (body: UpsertPlaceForm) => {
  try {
    const { data } = await privateAxios.post("/places", body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useCreatePlaceMutation = () => {
  return useMutation({
    mutationFn: (data: UpsertPlaceForm) => createPlace(data),
  });
};

export const updatePlace = async (id: string, body: UpsertPlaceForm) => {
  try {
    const { data } = await privateAxios.patch(`/places/${id}`, body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useUpdatePlaceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpsertPlaceForm }) =>
      updatePlace(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userPlacesQueryKeys.me() });
    },
  });
};

export const deletePlace = async (id: string) => {
  try {
    const { data } = await privateAxios.delete(`/places/${id}`);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useDeletePlaceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deletePlace(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userPlacesQueryKeys.me() });
    },
  });
};

export const setOnlineStatus = async (id: string, isOnline: boolean) => {
  try {
    const { data } = await privateAxios.patch(`/places/online/${id}`, {
      isOnline,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useSetOnlineStatusMutation = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ isOnline }: { isOnline: boolean }) =>
      setOnlineStatus(id, isOnline),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userPlacesQueryKeys.place(id),
      });
    },
  });
};
